import { useInjection } from 'libs/uikit/ioc/React';
import { useEvent } from 'libs/uikit/lib';
import { useToaster } from 'libs/uikit/lib/Toast/ToastProvider';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { IAuthenticationStore } from '../../core/Abstractions/IAuthenticationStore';

/**
 * SessionExpirationSentinal is a component that renders nothing on the page
 * but is responsible for watching the authenticationStore and signing the
 * user out when their token expires.
 */
export const SessionExpirationSentinal = observer(() => {
  const authenticationStore = useInjection(IAuthenticationStore);

  const currentTokenExpiry = authenticationStore.currentTokenExpiry;

  const toaster = useToaster();

  // An event handler that notifies the user that their session has expired
  // and then logs them out of the application.
  // The logout() call isn't responsible for redirecting them to the login page.
  // The user may be on a public page when their session expires and so we don't
  // always want to redirect them.
  const onTokenExpired = useEvent(async () => {
    toaster.present({
      title: 'Your session has expired',
      description: 'Please login again',
      intent: 'warning',
    });

    await authenticationStore.logout();
  });

  useEffect(() => {
    if (currentTokenExpiry) {
      const expiresIn = currentTokenExpiry.getTime() - Date.now();

      const timeout = setTimeout(onTokenExpired, expiresIn);

      return () => clearTimeout(timeout);
    }
  }, [currentTokenExpiry, onTokenExpired]);

  // nothing to render because we're just here to cause side-effects
  // based on the authenticationStore's state.
  return null;
});
